import type { ListedProduct as Product, ProductBase } from '~/types/shop'
import type { WishlistItem } from '~/types/wishlist'
import type { CartProduct } from '~/types/cart'
import { UserRole, type User } from '~/types/account'
import type { Currency } from '~/types/locale'
import type { CheckoutTrackingParams, PageSection, CheckboxId } from '~/utils/checkoutTracking'
import type {GoogleTagManagerApi} from "#nuxt-scripts/registry/google-tag-manager"
import { getCanonicalCategory } from '~/utils/canonicalCategory'

// Pick the same canonical category we render in breadcrumbs/SEO so GTM
// `item_category` events match what the user sees on the page. ProductBase
// exposes the slug list as `.categorySlugs`; WishlistItem only carries a
// `.category` which the backend can return either as an array of slugs or as a
// single string. Pass an array straight through to the canonical-category picker;
// wrap a lone string into a one-element array.
const getCategory = (product: ProductBase | WishlistItem): string => {
  const productSlugs = (product as ProductBase).categorySlugs
  const wishlistCategory = (product as WishlistItem).category
  const slugs = productSlugs ?? (Array.isArray(wishlistCategory)
    ? wishlistCategory
    : wishlistCategory ? [wishlistCategory] : undefined)
  return getCanonicalCategory(slugs)
}

// added for integration with userpilot (pushing user to datalayer)
export function pushUserAuthentication (gtm: GoogleTagManagerApi | undefined, user: User): void {
  gtm?.dataLayer.push({
    event: 'userAuthentication',
    user: {
      name: `${user.firstName} ${user.lastName}`,
      email: user.email,
      createdAt: Date.now()
    }
  })
}
// UserPilot segmentation (AB#60396): push the account's raw market/locale/userType to the
// dataLayer on each authenticated page load. Values are sent as-is from the account ($auth.user)
// — Marketing handles all interpretation/mapping in UserPilot, so we do NOT remap (e.g. GB stays
// GB, not UK) or filter. `market` is the account culture's region (the assigned SU market),
// `locale` is the raw account culture, `userType` is the raw account role.
export function pushUserSegmentation (gtm: GoogleTagManagerApi | undefined, user: User): void {
  const isNewSubscriber = user.isNewSubscriber === true
  const userSegmentation: {[id:string]: unknown} = {
    userType: user.role,
    market: user.culture?.split('-')[1]?.toUpperCase() ?? '',
    locale: user.culture ?? '',
    paper_pumpkin_active: isNewSubscriber ? 'new' : 'current'
  }
  // paperPumpkinLapsed (AB#64110) is Customer-only and always a real boolean: a missing backend
  // value is false. Every other role omits the key. Same contract as Nuxt2.
  if (user.role === UserRole.Customer) {
    userSegmentation.paperPumpkinLapsed = user.isLapsedSubscriber === true
  }
  const dbwsStatus = user.demonstrator?.dbwsStatus
  if (typeof dbwsStatus === 'string' && dbwsStatus) {
    userSegmentation.dbws_status = dbwsStatus.toLowerCase()
  }
  gtm?.dataLayer.push({
    event: 'userSegmentation',
    userSegmentation
  })
}

// typically, the view_promotion event is designed to capture the view of a promotion itself
// but in the case of last chance and limited access categories, we want to capture the view of the category
export function pushViewPromotion (gtm: GoogleTagManagerApi | undefined, promotionId: string, promotionName: string, creativeName?: string, creativeSlot?: string, product?: ProductBase): void {
  gtm?.dataLayer.push({
    event: 'view_promotion',
    ecommerce: {
      creative_name: creativeName, // what led to the promotion being viewed (e.g. "Homepage Banner Free Shipping")
      creative_slot: creativeSlot, // Slot/Placement of the creative (e.g. "Homepage_Banner")
      promotion_id: promotionId,
      promotion_name: promotionName,
      items: product
        ? [
            {
              item_name: product.title || product.name,
              item_id: product.id || product.productId,
              price: product.itemPrice,
              item_category: getCategory(product)
            }
          ]
        : []
    }
  })
}

// select_promotion event usually happens before the purchase but after the view_promotion event
// typically, the promotion is selected (clicked on) from a list of promotions (e.g. "Homepage Banner Free Shipping")
export function pushSelectPromotion (gtm: GoogleTagManagerApi | undefined, promotionId: string, promotionName: string, creativeName?: string, creativeSlot?: string, product?: ProductBase): void {
  gtm?.dataLayer.push({
    event: 'select_promotion',
    ecommerce: {
      creative_name: creativeName, // what led to the promotion being viewed (e.g. "Homepage Banner Free Shipping")
      creative_slot: creativeSlot, // Slot/Placement of the creative (e.g. "Homepage_Banner")
      promotion_id: promotionId,
      promotion_name: promotionName,
      items: product
        ? [
            {
              item_name: product.title || product.name,
              item_id: product.id || product.productId,
              price: product.itemPrice,
              item_category: getCategory(product)
            }
          ]
        : []
    }
  })
}

export function pushViewCart (gtm: GoogleTagManagerApi | undefined, products: CartProduct[], currency: Currency): void {
  gtm?.dataLayer.push({
    event: 'view_cart',
    ecommerce: {
      currency,
      items: products?.map((product) => {
        return {
          item_name: product.title,
          item_id: product.id,
          price: product.itemPrice
        }
      })
    }
  })
}

export function pushAddToWishlist (gtm: GoogleTagManagerApi | undefined, item: WishlistItem, category?: string): void {
  gtm?.dataLayer.push({
    event: 'add_to_wishlist',
    ecommerce: {
      items: [{
        item_name: item.name,
        item_id: item.productId,
        price: item.itemPrice,
        item_category: category || getCategory(item)
      }]
    }
  })
}

export function pushSearchResults (gtm: GoogleTagManagerApi | undefined, products: Product[], currency: Currency, query: string): void {
  gtm?.dataLayer.push({
    event: 'search',
    ecommerce: {
      currency,
      search_term: query,
      items: products?.map((product) => {
        return {
          item_name: product.title,
          item_id: product.id,
          price: product.itemPrice,
          item_category: getCategory(product)
        }
      })
    }
  })
}

export function pushSignup (gtm: GoogleTagManagerApi | undefined, user: User): void {
  gtm?.dataLayer.push({
    event: 'sign_up',
    user: {
      name: `${user.firstName} ${user.lastName}`,
      email: user.email,
      createdAt: Date.now()
    }
  })
}

export function pushViewItemList (gtm: GoogleTagManagerApi | undefined, products: Product[], currency: Currency, category?: string, startIndex: number = 0): void {
  let productPosition = startIndex
  gtm?.dataLayer.push({
    event: 'view_item_list',
    ecommerce: {
      currency,
      items: products?.map((product) => {
        productPosition += 1
        return {
          item_name: product.title,
          item_id: product.id,
          price: product.itemPrice,
          item_category: category, // only use category that is passed in
          index: productPosition
        }
      })
    }
  })
}

export function pushSelectItem (gtm: GoogleTagManagerApi | undefined, product: Product, position: number, category?: string): void {
  gtm?.dataLayer.push({
    event: 'select_item',
    ecommerce: {
      items: [{
        item_name: product.title,
        item_id: product.id,
        price: product.itemPrice,
        item_category: category, // only use category that is passed in
        index: position
      }]
    }
  })
}

export function pushViewItem (gtm: GoogleTagManagerApi | undefined, product: Product, currency: Currency): void {
  gtm?.dataLayer.push({
    event: 'view_item',
    ecommerce: {
      currency,
      items: [{
        item_name: product.title,
        item_id: product.id,
        price: product.itemPrice,
        item_category: getCategory(product)
      }]
    }
  })
}

export function pushAddToCart (gtm: GoogleTagManagerApi | undefined, product: ProductBase, currency: Currency, quantity: number, category?: string, source?: string): void {
  gtm?.dataLayer.push({
    event: 'add_to_cart',
    ecommerce: {
      currency,
      value: product.itemPrice * quantity,
      items: [{
        item_name: product.title || product.name,
        item_id: product.id || product.productId,
        price: product.itemPrice,
        item_category: category,
        quantity
      }]
    },
    ...(source && { add_to_cart_source: source })
  })
}

export function pushRemoveFromCart (gtm: GoogleTagManagerApi | undefined, product: CartProduct, currency: Currency): void {
  gtm?.dataLayer.push({
    event: 'remove_from_cart',
    ecommerce: {
      currency,
      items: [{
        item_name: product.title || product.name,
        item_id: product.id || product.productId,
        price: product.itemPrice,
        quantity: product.quantity
      }]
    }
  })
}
export function pushCheckoutStart (gtm: GoogleTagManagerApi | undefined, products: CartProduct[], currency: Currency, params?: CheckoutTrackingParams): void {
  gtm?.dataLayer.push({
    event: 'checkout_started',
    ...(params ?? {}),
    ecommerce: {
      currency,
      items: products.map((item) => {
        return {
          item_id: item.productId,
          item_name: item.name,
          price: item.itemPrice,
          quantity: item.quantity
        }
      })
    }
  })
}

/**
 * The fields a purchase event actually reads off a confirmation.
 *
 * Narrowed from InvoiceConfirmation so the join flow can emit the same event from its own
 * JoinOrderConfirmation, which carries all of these under the same names but is not otherwise
 * the same type. One emitter rather than two means the two flows cannot drift on the payload
 * shape — a funnel that splits on `purchase` would be unreadable if they did.
 */
export interface PurchaseConfirmation {
  orderConfirmationNumber: string
  orderTotalNumeric: number
  taxAmountNumeric: number
  shippingAmountNumeric: number
  items?: Array<{ itemId: string, description: string, price: string | number, quantity: number }>
}

export function pushCheckoutComplete (gtm: GoogleTagManagerApi | undefined, invoiceConfirmation: PurchaseConfirmation, currency: Currency, params?: CheckoutTrackingParams): void {
  gtm?.dataLayer.push({
    event: 'purchase',
    ...(params ?? {}),
    ecommerce: {
      currency,
      transaction_id: invoiceConfirmation.orderConfirmationNumber,
      value: invoiceConfirmation.orderTotalNumeric,
      tax: invoiceConfirmation.taxAmountNumeric,
      shipping: invoiceConfirmation.shippingAmountNumeric,
      items: invoiceConfirmation.items?.map((item) => {
        return {
          item_id: item.itemId,
          item_name: item.description,
          price: item.price,
          quantity: item.quantity
        }
      })
    }
  })
}

export function pushCheckoutStepView (gtm: GoogleTagManagerApi | undefined, params: CheckoutTrackingParams, pageSection: PageSection): void {
  gtm?.dataLayer.push({ event: 'checkout_step_view', ...params, page_section: pageSection })
}

export function pushCheckoutStepCompleted (gtm: GoogleTagManagerApi | undefined, params: CheckoutTrackingParams, pageSection: PageSection): void {
  gtm?.dataLayer.push({ event: 'checkout_step_completed', ...params, page_section: pageSection })
}

export function pushCheckoutCheckboxChecked (gtm: GoogleTagManagerApi | undefined, params: CheckoutTrackingParams, pageSection: PageSection, checkboxId: CheckboxId): void {
  gtm?.dataLayer.push({ event: 'checkout_checkbox_checked', ...params, page_section: pageSection, checkbox_id: checkboxId })
}

export function pushUIInteraction (gtm: GoogleTagManagerApi | undefined, actionName: string, elementType: string, targetId?: string, additionalData?: Record<string, unknown>): void {
  gtm?.dataLayer.push({
    event: 'ui_interaction',
    action_name: actionName,
    element_type: elementType,
    ...(targetId && { target_id: targetId }),
    ...additionalData
  })
}

export interface BreadcrumbClick {
  label: string
  url: string
  // 1-based position in the full trail, so a crumb hidden behind the collapsed
  // ellipsis still reports where it sits in the chain.
  position: number
  count: number
  // Product detail page only: the product the shopper stepped back from, and the
  // canonical category its trail shows (AB#63832).
  itemId?: string
  itemCategory?: string
}

// A shopper clicked a breadcrumb to step back up the category trail. Ported
// from nuxt2 utils/gtm.ts (AB#63904, AB#63978).
export function pushBreadcrumbClick (gtm: GoogleTagManagerApi | undefined, click: BreadcrumbClick): void {
  gtm?.dataLayer.push({
    event: 'breadcrumb_click',
    crumb_label: click.label,
    crumb_url: click.url,
    crumb_position: click.position,
    crumb_count: click.count,
    ...(click.itemId && { item_id: click.itemId }),
    ...(click.itemCategory && { item_category: click.itemCategory })
  })
}

export function pushVideoInteraction (gtm: GoogleTagManagerApi | undefined, actionName: string, videoContext?: string, additionalData?: Record<string, unknown>): void {
  gtm?.dataLayer.push({
    event: 'video_interaction',
    action_name: actionName,
    element_type: 'play_button',
    ...(videoContext && { video_context: videoContext }),
    ...additionalData
  })
}
